import { useComments } from "app/uicomponents/macrocomponents/Comments/CommentContext";
import CommentLayout from "app/uicomponents/macrocomponents/Comments/CommentLayout";
import { AnimatedCheckbox } from "app/uicomponents/microcomponents/AnimatedChecbox";
import ScreenLoader from "app/uicomponents/microcomponents/ScreenLoader";
import { makeRequest } from "app/utils/makeRequest";
import { getObjectFromSessionStore, getSessionStore } from "app/utils/sessionStore";
import { CurrentProposalType, PROPOSAL_STATUSES, ProposalTypes, THEME } from "app/utils/types";
import {
  createGradient,
  formatToCompactNumber,
  getThemeColors,
  roundCompactThousands,
} from "app/utils/utils";
import { AxiosResponse } from "axios";
import { getChartLabels, isKeyPersonInsurance, isSplitDollar } from "lib/utils";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import { Divider } from "primereact/divider";
import { Tooltip } from "primereact/tooltip";
import {
  calendarTooltipIconSvg,
  planYearTooltipIconSvg,
  insuredAgeTooltipIconSvg,
} from "app/utils/chartTooltipIcons";
import { useCallback, useEffect, useRef, useState } from "react";
import ChartFilter, { getTooltipLabel } from "../Common/ChartFilter";
import { useIPContext } from "../InteractiveProposalProvider";
import PFChart, { PremiumFinanceChartsDataProps } from "./Summary/PFChart";
import PFTooltip from "./Summary/PFTooltip";

interface Shareholder {
  id: string;
  name: string;
  lifePolicyCount?: number;
  deathBenefit?: number;
  disabilityPolicyCount?: number;
  disabilityBenefit?: number;
  ciBenefit?: number;
  firstName?: string;
  lastName?: string;
}

interface ValuationData {
  planYear: number;
  value: number;
  income?: number;
  db?: number;
  insuredAge?: number;
  year?: number;
}

interface SummaryInsuredData {
  totalLoanedPremium: string;
  deathBenefitYear1: string;
  projectedAnnualIncome: string;
  beginningYear: string;
  firstName: string;
  lastName: string;
}

interface PremiumFinanceSummaryData {
  totalLoan: string;
  totalClientExpenses: string;
  leverage: string;
  year1Rate: string;
  blendedRate: string;
  financialImpact: string;
  interestRate: { planYear: number; interestRate: number }[];
}

const InteractiveProposalSummary = () => {
  const [proposalValuation, setProposalValuation] = useState<ValuationData[]>([]);
  const [shareholders, setShareholders] = useState<Shareholder[]>([]);
  const [selectedShareholders, setSelectedShareholders] = useState<Shareholder[]>([]);
  const [insuredDetails, setInsuredDetails] = useState<[]>([]);
  const [insuredId, setInsuredId] = useState<string[]>([]);
  const [sdChartsData, setSdChartsData] = useState<any>(null);
  const [premiumFinanceChartsData, setPremiumFinanceChartsData] = useState<
    PremiumFinanceChartsDataProps[]
  >([]);
  const {
    selectedGrowthRate,
    selectedChartFilterType,
    selectedVersion,
    proposalStatus,
    selectedChartFilter,
    setSelectedChartFilter,
    setInsuredDisabled,
  } = useIPContext();
  const [summaryInsuredData, setSummaryInsuredData] = useState<SummaryInsuredData[]>([]);
  const [premiumFinanceSummaryData, setPremiumFinanceSummaryData] =
    useState<PremiumFinanceSummaryData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const planInfo = getSessionStore("planInfo");
  const currentProposalType = getSessionStore("currentProposalType");
  const globalTheme = getSessionStore("globalTheme");
  const { branding } = getSessionStore("lookUpResponse");
  const { primaryColor, ipTooltipColor, secondaryColor } = getThemeColors(branding);
  const proposalId =
    selectedVersion?.id ??
    getSessionStore("proposalID") ??
    getSessionStore("interactiveProposalID");
  const [isFetchingData, setIsFetchingData] = useState<boolean>(false);
  const selectedSeriesRef = useRef<string | null>(null);
  const zoomStateRef = useRef<{ start: number; end: number } | null>(null);

  const isPlanOrInactive = [
    PROPOSAL_STATUSES.PLAN_CREATED,
    PROPOSAL_STATUSES.REVOKED,
    PROPOSAL_STATUSES.IN_ACTIVE,
  ].includes(proposalStatus as any);

  const isDarkMode = globalTheme === THEME.DARK;

  const proposalType =
    currentProposalType === CurrentProposalType.PLANS
      ? planInfo?.planType
      : getSessionStore("selectedProposalRow")?.proposalType ||
        getObjectFromSessionStore("proposalType");

  const { setIsCurrPageLoading } = useComments();

  const [selectedDataTypes, setSelectedDataTypes] = useState<string[]>(["income", "db"]);

  const formatNumber = (value: number) => {
    if (value >= 1000000000) {
      return `$${(value / 1000000000).toFixed(2)}B`;
    }
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    }
    if (value >= 1000) {
      return `$${Math.round(value / 1_000)}K`;
    }
    return `$${value}`;
  };

  useEffect(() => {
    if (proposalType === ProposalTypes.SplitDollar) {
      setInsuredDisabled(false);
    } else {
      if (proposalType === ProposalTypes.PremiumFinance) {
        setInsuredDisabled(insuredId?.length !== 1);
      }
      if (proposalType !== ProposalTypes.PremiumFinance) {
        setInsuredDisabled(selectedShareholders?.length !== 1);
      }
    }
  }, [selectedShareholders, insuredId, proposalType]);

  const calculateChartData = useCallback(
    (chartsDataList: any[], funding: string[], brandingColor: string) => {
      const data = chartsDataList?.map((item: any) => {
        let total = 0;
        if (funding.includes("income")) {
          total += item?.income || 0;
        }
        if (funding.includes("db")) {
          total += item?.db || 0;
        }
        return total;
      });

      const datasets = [
        {
          label: "Benefits",
          data,
          fill: true,
          borderColor: brandingColor,
          tension: 0.4,
          backgroundColor: (context: any) => createGradient(context, brandingColor),
        },
      ];

      return datasets;
    },
    [],
  );

  const fetchDataBasedOnShareholders = async (selectedShareholdersIds?: string[]) => {
    setIsFetchingData(true);
    try {
      const response = await makeRequest("interactiveProposalSummary").get(
        `${proposalId}?insuredIds=${JSON?.stringify(selectedShareholdersIds || [])}`,
        true,
        false,
      );
      setProposalValuation(response?.data?.data?.proposalValuation?.slice(0, 10) || []);
      setIsFetchingData(false);
    } catch (error) {
      setIsFetchingData(false);
    }
  };

  const fetchData = async (ids: string[] = []) => {
    setIsLoading(true);
    let response: AxiosResponse<any, any>;
    try {
      setIsCurrPageLoading(true);
      if (proposalType === ProposalTypes.PremiumFinance) {
        const queryParams = [];
        if (selectedChartFilterType === "runningBalance") {
          queryParams.push(`growthRate=${selectedGrowthRate}`);
        }
        if (ids.length > 0 || insuredId?.length > 0) {
          queryParams.push(`insuredIds=${JSON?.stringify(ids.length > 0 ? ids : insuredId || [])}`);
        }
        response = await makeRequest("interactiveProposalSummary").get(
          `${proposalId}${queryParams.length > 0 ? `?${queryParams.join("&")}` : ""}`,
          true,
          false,
        );
        setPremiumFinanceChartsData(response?.data?.data?.graphData);
        setPremiumFinanceSummaryData({
          totalLoan: response?.data?.data?.totalLoan,
          totalClientExpenses: response?.data?.data?.totalClientExpenses,
          leverage: response?.data?.data?.leverage,
          year1Rate: response?.data?.data?.year1Rate,
          blendedRate: response?.data?.data?.blendedRate,
          financialImpact: response?.data?.data?.financialImpact,
          interestRate: response?.data?.data?.interestRate,
        });
        setInsuredDetails(response?.data?.data?.insuredDetails);
      } else {
        response = await makeRequest("interactiveProposalSummary").get(proposalId, true, false);
      }
      setIsCurrPageLoading(false, true);
      const fetchedShareholders = isKeyPersonInsurance(proposalType)
        ? response?.data?.data?.employees
        : isSplitDollar(proposalType)
        ? response?.data?.data?.insuredDetails
        : response?.data?.data?.shareholders;
      if (isSplitDollar(proposalType)) {
        setSummaryInsuredData([
          {
            totalLoanedPremium: response?.data?.data?.totalLoanedPremium || "",
            deathBenefitYear1: response?.data?.data?.deathBenefitYear1 || "",
            projectedAnnualIncome: response?.data?.data?.projectedAnnualIncome || "",
            beginningYear: response?.data?.data?.beginningYear || "",
            firstName: response?.data?.data?.primaryContact?.firstName || "",
            lastName: response?.data?.data?.primaryContact?.lastName || "",
          },
        ]);
      }

      if (proposalType !== ProposalTypes.SplitDollar) {
        const getIds = fetchedShareholders?.map((item: any) => item.id);
        fetchDataBasedOnShareholders(getIds);
      }
      setShareholders(fetchedShareholders);
      setSelectedShareholders(fetchedShareholders);
      setProposalValuation(response?.data?.data?.proposalValuation?.slice(0, 15) || []);

      if (isSplitDollar(proposalType)) {
        const data = calculateChartData(
          response?.data?.data?.proposalValuation,
          selectedDataTypes,
          primaryColor,
        );
        setSdChartsData(data);
      }
    } catch (error) {
      setShareholders([]);
      setSelectedShareholders([]);
      setProposalValuation([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (proposalType !== ProposalTypes.PremiumFinance) {
      fetchData();
    }
  }, []);

  useEffect(() => {
    if (isSplitDollar(proposalType)) {
      const data = calculateChartData(proposalValuation, selectedDataTypes, primaryColor);
      setSdChartsData(data);
    }
  }, [selectedDataTypes, proposalValuation, primaryColor, proposalType]);

  const handleCheckboxChange = (shareholder: Shareholder) => {
    setSelectedChartFilter("year");
    setSelectedShareholders((prevSelected) => {
      const isCurrentlySelected = prevSelected.some((s) => s.id === shareholder.id);
      let newSelected;

      if (isCurrentlySelected) {
        // Remove the shareholder
        newSelected = prevSelected.filter((s) => s.id !== shareholder.id);
      } else {
        // Add the shareholder
        newSelected = [...prevSelected, shareholder];
      }
      // Only call fetchDataBasedOnShareholders if there's exactly one selected shareholder
      if (newSelected?.length > 0) {
        // Pass the ID of the single selected shareholder
        const getIds = newSelected?.map((item: any) => item?.id);
        fetchDataBasedOnShareholders(getIds);
      } else if (newSelected.length === 0) {
        // Pass empty array if no shareholders are selected
        fetchDataBasedOnShareholders([]);
      }
      // If there are multiple shareholders selected, don't call fetchDataBasedOnShareholders

      return newSelected;
    });
  };

  const handlePremiumFinanceCheckboxChange = (insured: any) => {
    setInsuredId((prevSelected: string[]) => {
      const isCurrentlySelected = prevSelected.some((s) => s === insured.userId);
      let newSelected;

      if (isCurrentlySelected) {
        // Remove the id
        newSelected = prevSelected.filter((s) => s !== insured.userId);
      } else {
        // Add the id
        newSelected = [...prevSelected, insured.userId];
      }
      if (newSelected?.length > 0) {
        // Pass the ID of the single selected shareholder
        fetchData(newSelected);
      } else if (newSelected.length === 0) {
        // Pass empty array if no ids are selected
        fetchData([]);
      }

      return newSelected;
    });
  };

  useEffect(() => {
    if (proposalType === ProposalTypes.PremiumFinance) {
      setIsLoading(true);
      makeRequest("interactiveProposalSummaryInsureds")
        .get(proposalId, true, false)
        .then((res) => {
          const getInsuredIds = res?.data?.data?.map((item: any) => item.userId);
          setInsuredId(getInsuredIds);
          setTimeout(() => {
            fetchData(getInsuredIds);
          }, 1000);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [selectedGrowthRate]);

  // Calculate total sum for selected shareholders
  const totalLifePolicyCount = selectedShareholders?.reduce(
    (sum, s) => sum + (s.lifePolicyCount || 0),
    0,
  );
  const totalDeathBenefit = selectedShareholders?.reduce(
    (sum, s) => sum + (+(s.deathBenefit || 0) + +(s.ciBenefit || 0) || 0),
    0,
  );
  const totalDisabilityPolicyCount = selectedShareholders?.reduce(
    (sum, s) => sum + (s.disabilityPolicyCount || 0),
    0,
  );
  const totalDisabilityBenefit = selectedShareholders?.reduce(
    (sum, s) => sum + (s.disabilityBenefit || 0),
    0,
  );

  // Add this function to format the chart data
  const getChartData = () => {
    const chartData = {
      labels: getChartLabels(proposalValuation, selectedChartFilter),
      datasets: [
        {
          label: "Valuation",
          data: proposalValuation.map((item: any) => item.value),
          fill: true,
          borderColor: primaryColor,
          tension: 0.4,
          backgroundColor: (context: any) => createGradient(context, primaryColor),
        },
      ],
    };
    return chartData;
  };
  // Add chart options
  const chartOptions = {
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: ipTooltipColor,
        padding: 12,
        titleFont: { size: 14 },
        bodyFont: { size: 14 },
        displayColors: false,
        callbacks: {
          title: (items: any) => `${getTooltipLabel(selectedChartFilter)} ${items[0].label}`,
          label: (context: any) => formatNumber(context.raw),
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
      },
      y: {
        display: false,
        beginAtZero: true,
        grid: {
          display: false,
        },
      },
    },
    elements: {
      point: {
        radius: 0,
        backgroundColor: primaryColor,
      },
      line: {
        tension: 0.4,
        borderWidth: 2,
      },
    },
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: "index",
    },
  };

  // Benefits Received hover rebuilt as an external HTML tooltip so it matches the
  // funding/projection tab hovers: IAPP-icon header (calendar year | plan year | insured
  // age) above per-series rows and a total (Aug 4 feedback: "same style hover").
  const sdBenefitsExternalTooltip = (context: any) => {
    const { chart, tooltip } = context;
    let el = document.getElementById("sd-benefits-tooltip");
    if (!el) {
      el = document.createElement("div");
      el.id = "sd-benefits-tooltip";
      el.style.cssText =
        "position:absolute;pointer-events:none;z-index:9999;transition:opacity 0.1s ease;";
      document.body.appendChild(el);
    }
    if (!tooltip || tooltip.opacity === 0) {
      el.style.opacity = "0";
      return;
    }
    const dataIndex = tooltip.dataPoints?.[0]?.dataIndex ?? 0;
    const item: any = proposalValuation[dataIndex] || {};
    const iconColor = isDarkMode ? "#ffffff" : "#1F2937";
    const divider = `<div style="width:1px;height:20px;background:${
      isDarkMode ? "#ffffff" : "#9CA3AF"
    };opacity:0.4;"></div>`;
    const headerCell = (icon: string, text: string) =>
      `<div style="display:flex;align-items:center;gap:6px;">${icon}<span style="font-weight:bold;font-size:15px;">${text}</span></div>`;
    const headerCells = [
      item?.year ? headerCell(calendarTooltipIconSvg(iconColor, 15), String(item.year)) : "",
      item?.planYear
        ? headerCell(planYearTooltipIconSvg(iconColor, 16), String(item.planYear))
        : "",
      item?.insuredAge
        ? headerCell(insuredAgeTooltipIconSvg(iconColor, 18), `A${item.insuredAge}`)
        : "",
    ].filter(Boolean);
    const header = `<div style="background:${isDarkMode ? "#3A3A50" : "#E5E7EB"};color:${
      isDarkMode ? "#ffffff" : "#374151"
    };padding:8px 12px;text-align:center;font-weight:bold;font-family:'Conthrax Semi-Bold','Arial',sans-serif;border-bottom:1px solid ${
      isDarkMode ? "#4A4A60" : "#e0e0e0"
    };border-radius:8px 8px 0 0;display:flex;justify-content:center;align-items:center;gap:18px;">${headerCells.join(
      divider,
    )}</div>`;

    const rows: Array<{ label: string; value: number; color: string }> = [];
    if (selectedDataTypes.includes("income")) {
      rows.push({ label: "Income", value: item?.income || 0, color: primaryColor });
    }
    if (selectedDataTypes.includes("db")) {
      rows.push({ label: "Death Benefit", value: item?.db || 0, color: `${primaryColor}cc` });
    }
    const total = rows.reduce((sum, row) => sum + row.value, 0);
    const rowHtml = (label: string, value: string, bullet?: string) =>
      `<div style="display:flex;justify-content:space-between;align-items:center;padding:4px 8px;margin:2px 0;border-bottom:1px solid ${
        isDarkMode ? "#4A4A60" : "#E5E7EB"
      };"><div style="display:flex;align-items:center;gap:8px;">${
        bullet
          ? `<div style="width:8px;height:8px;border-radius:50%;background-color:${bullet};flex-shrink:0;"></div>`
          : ""
      }<span style="color:${
        isDarkMode ? "#ffffff" : "#374151"
      };font-size:12px;">${label}</span></div><span style="color:${
        isDarkMode ? "#ffffff" : "#374151"
      };margin-left:12px;font-weight:bold;font-size:12px;">${value}</span></div>`;
    let content = '<div style="padding:8px 12px;">';
    if (rows.length === 0) {
      content += rowHtml("No data selected", "");
    } else {
      rows.forEach((row) => {
        content += rowHtml(row.label, formatNumber(row.value), row.color);
      });
      if (rows.length > 1) {
        content += rowHtml("Total", formatNumber(total));
      }
    }
    content += "</div>";

    el.innerHTML = `<div style="background:${isDarkMode ? "#2A2A3D" : "#ffffff"};border:1px solid ${
      isDarkMode ? "#3A3A50" : "#E5E7EB"
    };border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,0.15);overflow:hidden;min-width:220px;">${header}${content}</div>`;

    const rect = chart.canvas.getBoundingClientRect();
    el.style.opacity = "1";
    el.style.left = `${rect.left + window.scrollX + tooltip.caretX + 12}px`;
    el.style.top = `${rect.top + window.scrollY + tooltip.caretY - 24}px`;
  };

  const splitDollarChartOptions = {
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: sdBenefitsExternalTooltip,
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
      },
      y: {
        // Aug 4 feedback: the Benefits chart needs dollar amounts on the Y axis.
        display: true,
        ticks: {
          callback: (value: any) => formatNumber(Number(value)),
        },
        beginAtZero: true,
        grid: {
          display: false,
        },
      },
    },
    elements: {
      point: {
        radius: 0,
        backgroundColor: primaryColor,
      },
      line: {
        tension: 0.4,
        borderWidth: 2,
      },
    },
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: "index",
    },
  };

  // Function to handle checkbox changes
  const handleDataTypeChange = (dataType: string) => {
    setSelectedDataTypes((prevSelected) =>
      prevSelected.includes(dataType)
        ? prevSelected.filter((type) => type !== dataType)
        : [...prevSelected, dataType],
    );
  };

  return (
    <>
      <style>
        {`
          .leverage-tooltip .p-tooltip-text,
          .year1-tooltip .p-tooltip-text,
          .blended-tooltip .p-tooltip-text,
          .financial-impact-tooltip .p-tooltip-text {
            white-space: pre-line !important;
            line-height: 1.4 !important;
            padding: 12px 16px !important;
            font-size: 13px !important;
          }
          
          .p-tooltip-text {
            white-space: pre-line !important;
          }
          
          [data-pc-section="text"] {
            white-space: pre-line !important;
          }
        `}
      </style>
      <CommentLayout commentLayoutId="interactive-proposal-summary" className="p-2 mt-8">
        {isLoading ? (
          <ScreenLoader />
        ) : (
          <div className="flex gap-4">
            <div className="w-1/2">
              <Card
                className="h-full rounded-2xl shadow-lg dark:bg-[#2A2A3D]"
                pt={{
                  body: {
                    className: "py-0 px-3",
                  },
                }}
              >
                <div className="flex flex-col h-full">
                  <h1 className="text-lg font-bold mb-2 conthrax-font">
                    {isKeyPersonInsurance(proposalType)
                      ? "Key Person"
                      : isSplitDollar(proposalType) || proposalType === ProposalTypes.PremiumFinance
                      ? "Insured Person(s)"
                      : "Shareholders"}
                  </h1>
                  <div className="flex flex-wrap gap-2 max-h-[140px] overflow-y-auto">
                    {shareholders
                      ? shareholders.map((shareholder, index) => (
                          <div key={index}>
                            <AnimatedCheckbox
                              checked={selectedShareholders.some((s) => s.id === shareholder.id)}
                              onChange={() => handleCheckboxChange(shareholder)}
                              inputId={shareholder.id}
                              label={
                                proposalType !== ProposalTypes.SplitDollar
                                  ? shareholder.name?.split(",")?.reverse()?.join(" ")
                                  : `${shareholder.firstName} ${shareholder.lastName}`
                              }
                              brandingColor={isDarkMode ? secondaryColor : primaryColor}
                              isDisabled={isPlanOrInactive}
                              ishideIndicator={isSplitDollar(proposalType)}
                            />
                          </div>
                        ))
                      : proposalType === ProposalTypes.PremiumFinance
                      ? insuredDetails.map(
                          (
                            insured: {
                              user: {
                                id: string;
                                profile: { firstName: string; lastName: string };
                              };
                            },
                            index: number,
                          ) => (
                            <div key={index}>
                              <AnimatedCheckbox
                                checked={insuredId.includes(insured.user.id)}
                                onChange={() => handlePremiumFinanceCheckboxChange(insured)}
                                inputId={insured.user.id}
                                label={`${insured.user.profile.firstName} ${insured.user.profile.lastName}`}
                                brandingColor={isDarkMode ? secondaryColor : primaryColor}
                                isDisabled={isPlanOrInactive}
                                ishideIndicator={isSplitDollar(proposalType)}
                              />
                            </div>
                          ),
                        )
                      : summaryInsuredData?.[0]?.firstName && (
                          <AnimatedCheckbox
                            checked={false} // Default unchecked as there's no ID to match
                            onChange={() => {}}
                            inputId="summaryInsuredData"
                            label={`${summaryInsuredData[0].firstName} ${summaryInsuredData[0].lastName}`}
                            brandingColor={isDarkMode ? secondaryColor : primaryColor}
                            isDisabled={isPlanOrInactive}
                            ishideIndicator={isSplitDollar(proposalType)}
                          />
                        )}
                  </div>
                  <Divider />
                  {/* <hr className="my-2 border" /> */}

                  <div className="border rounded-lg mt-2 p-3">
                    {proposalType === ProposalTypes.PremiumFinance ? (
                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-left">
                          <p className="text-2xl conthrax-font">
                            {premiumFinanceSummaryData?.totalLoan &&
                              formatToCompactNumber(premiumFinanceSummaryData?.totalLoan, {
                                prefix: "$",
                              })}
                          </p>
                          <p className="text-base text-[#5c6779]">Total Financing</p>
                        </div>
                        <div className="text-left">
                          <p className="text-2xl conthrax-font">
                            {premiumFinanceSummaryData?.totalClientExpenses &&
                              formatToCompactNumber(
                                premiumFinanceSummaryData?.totalClientExpenses,
                                { prefix: "$" },
                              )}
                          </p>
                          <p className="text-base text-[#5c6779]">Total Client Expenses</p>
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-1">
                            <p className="text-2xl conthrax-font">
                              {premiumFinanceSummaryData?.leverage || 0}X
                            </p>
                            <PFTooltip type="leverage" />
                          </div>
                          <div className="flex items-center gap-1">
                            <p className="text-base text-[#5c6779]">Effective Leverage</p>
                          </div>
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-1">
                            <p className="text-2xl conthrax-font">
                              {Number(premiumFinanceSummaryData?.year1Rate).toFixed(2)}%
                            </p>
                            <PFTooltip
                              type="year1Rate"
                              interestRate={premiumFinanceSummaryData?.interestRate}
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <p className="text-base text-[#5c6779]">Year 1 Rate</p>
                          </div>
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-1">
                            <p className="text-2xl conthrax-font">
                              {Number(premiumFinanceSummaryData?.blendedRate).toFixed(2)}%
                            </p>
                            <PFTooltip
                              type="blendedRate"
                              interestRate={premiumFinanceSummaryData?.interestRate}
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <p className="text-base text-[#5c6779]">Blended Rate</p>
                          </div>
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-1">
                            <p className="text-2xl conthrax-font">
                              {premiumFinanceSummaryData?.financialImpact &&
                                formatToCompactNumber(premiumFinanceSummaryData?.financialImpact, {
                                  prefix: "$",
                                })}
                            </p>
                            <PFTooltip type="financialImpact" />
                          </div>
                          <div className="flex items-center gap-1">
                            <p className="text-base text-[#5c6779]">Financial Impact</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="text-left">
                          <p className="text-2xl conthrax-font">
                            {isSplitDollar(proposalType)
                              ? `$${roundCompactThousands(
                                  summaryInsuredData?.[0]?.totalLoanedPremium,
                                )}`
                              : totalLifePolicyCount}
                          </p>
                          <p className="text-base text-[#5c6779]">
                            {isSplitDollar(proposalType)
                              ? "Total Financed Premium"
                              : "Life Policies"}
                          </p>
                        </div>
                        <div className="text-left">
                          <p className="text-2xl conthrax-font">
                            {isSplitDollar(proposalType)
                              ? `$${roundCompactThousands(
                                  summaryInsuredData?.[0]?.deathBenefitYear1,
                                )}`
                              : formatNumber(totalDeathBenefit)}
                          </p>
                          <p className="text-base text-[#5c6779]">
                            {isSplitDollar(proposalType)
                              ? "Death Benefit Year 1"
                              : "Life Policy Benefits"}
                          </p>
                        </div>
                        <div className="text-left">
                          <p className="text-2xl conthrax-font">
                            {isSplitDollar(proposalType)
                              ? `$${roundCompactThousands(
                                  summaryInsuredData?.[0]?.projectedAnnualIncome,
                                )}`
                              : totalDisabilityPolicyCount}
                          </p>
                          <p className="text-base text-[#5c6779]">
                            {isSplitDollar(proposalType)
                              ? "Projected Annual Income"
                              : "Disability Policies"}
                          </p>
                        </div>
                        <div className="text-left">
                          <p className="text-2xl conthrax-font">
                            {isSplitDollar(proposalType)
                              ? summaryInsuredData?.[0]?.beginningYear
                              : formatNumber(totalDisabilityBenefit)}
                          </p>
                          <p className="text-base text-[#5c6779]">
                            {isSplitDollar(proposalType)
                              ? "Beginning Year"
                              : "Disability Policy Benefits"}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </div>
            <div className="w-1/2">
              <Card
                className="h-full rounded-2xl shadow-lg dark:bg-[#2A2A3D]"
                pt={{
                  body: {
                    className: "py-0 px-3",
                  },
                }}
              >
                {proposalType !== ProposalTypes.SplitDollar ? (
                  <div className="flex flex-col h-full">
                    <Tooltip
                      target=".tooltip-target"
                      content="Represents the estimated financial impact and potential exposure from the loss of selected key person(s)."
                      className="max-w-[600px]"
                      position="left"
                    />
                    <Tooltip
                      target=".valuation-tooltip-target"
                      content="Estimated equity value used in buy-sell planning for selected shareholder(s)."
                      className="max-w-[600px]"
                      position="left"
                    />
                    <div className="flex justify-between items-center">
                      <h1
                        className={`text-lg font-bold mb-2 conthrax-font ${
                          isKeyPersonInsurance(proposalType)
                            ? "tooltip-target"
                            : proposalType === ProposalTypes.PremiumFinance
                            ? ""
                            : "valuation-tooltip-target"
                        }`}
                      >
                        {isKeyPersonInsurance(proposalType) ? (
                          "Estimated Exposure"
                        ) : proposalType === ProposalTypes.PremiumFinance ? (
                          <div>
                            <span>Performance Overview</span>
                            <Tooltip
                              target=".roi-info-tooltip"
                              className="max-w-[650px] text-[13px]"
                              position="left"
                              showDelay={300}
                              hideDelay={200}
                            >
                              <div>
                                <p>
                                  <span className="font-semibold mr-1">Hidden Delta™</span>
                                  is the extra wealth you build when you choose to finance your life
                                  insurance premiums instead of paying them out of pocket.
                                </p>
                                <p>
                                  Rather than using your own cash, you keep that money invested —
                                  and over time, it grows. The result? You maintain full insurance
                                  protection while potentially creating millions of dollars in
                                  additional wealth.
                                </p>
                                <p>
                                  That gap — between what you would have spent and what you kept
                                  growing — is your Hidden Delta.
                                </p>
                              </div>
                              <p className="mt-2">
                                <span className="font-semibold mr-1">Financial Impact</span>
                                represents the total financial impact of the strategy, combining all
                                income received over the years with the final death benefit payout
                                at the insured&apos;s age 95. It illustrates the long-term value and
                                wealth amplification achieved through premium financing.
                              </p>
                              <p className="mt-2">
                                <span className="font-semibold mr-1">Net Death Benefit</span>
                                represents the estimated death benefit remaining after the
                                outstanding financing balance is satisfied at death. It reflects the
                                net insurance benefit available after repayment of the financing
                                obligation.
                              </p>
                              <p className="mt-2">
                                <span className="font-semibold mr-1">ROI</span>
                                reflects the total value the client&apos;s heirs receive at death
                                compared to the client&apos;s actual out-of-pocket costs. It
                                measures the increase received during the life of the plan plus the
                                net death benefit, relative to the client&apos;s total out-of-pocket
                                expenses. This provides a mortality-based view of the
                                strategy&apos;s overall return when evaluated at death rather than
                                surrender.
                              </p>
                              <div className="mt-2">
                                <p className="font-semibold">Disclaimer:</p>
                                <p>
                                  The terms and conditions outlined in this premium finance proposal
                                  are based on current market conditions and are subject to change
                                  prior to loan closing. The final loan terms, including interest
                                  rates, collateral requirements, and other associated conditions,
                                  may vary based on factors such as underwriting results, changes in
                                  market conditions, the insured&apos;s rating class, and additional
                                  financial or legal considerations. Any adjustments will be
                                  communicated promptly and will be contingent upon final approval
                                  from the lending institution. The proposal does not constitute a
                                  binding offer and should not be relied upon as a guarantee of the
                                  terms outlined herein.
                                </p>
                              </div>
                            </Tooltip>
                            <i
                              className="pi pi-info-circle roi-info-tooltip ml-2 cursor-help text-lg text-[#2A7FFF]"
                              id="roi-info-tooltip"
                            />
                          </div>
                        ) : (
                          "Valuation"
                        )}
                      </h1>
                      <ChartFilter />
                    </div>
                    <div className="flex-grow">
                      <div className="relative h-[270px]">
                        {isFetchingData ? (
                          <div className="flex justify-center items-center h-full">
                            <ScreenLoader />
                          </div>
                        ) : proposalType === ProposalTypes.PremiumFinance ? (
                          <PFChart
                            chartsData={premiumFinanceChartsData}
                            selectedChartFilter={selectedChartFilter}
                            selectedSeriesRef={selectedSeriesRef}
                            zoomStateRef={zoomStateRef}
                          />
                        ) : (
                          proposalValuation.length > 0 && (
                            <Chart
                              type="line"
                              data={getChartData()}
                              options={chartOptions}
                              className="w-full h-full"
                            />
                          )
                        )}
                      </div>
                    </div>
                    {proposalType !== ProposalTypes.PremiumFinance && (
                      <div className="flex justify-between mt-2">
                        <div>
                          <p className="text-2xl font-bold conthrax-font">
                            {proposalValuation[0] ? formatNumber(proposalValuation[0].value) : 0}
                          </p>
                          <p className="text-base text-[#5c6779]">
                            {isKeyPersonInsurance(proposalType)
                              ? "Estimated Exposure YR"
                              : "Estimated Valuation Year "}
                            1
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold conthrax-font">
                            {proposalValuation.length
                              ? formatNumber(proposalValuation[proposalValuation.length - 1].value)
                              : 0}
                          </p>
                          <p className="text-base text-[#5c6779]">
                            {isKeyPersonInsurance(proposalType)
                              ? "Estimated Exposure YR"
                              : "Estimated Valuation Year "}
                            10
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="pb-4 pt-1">
                      <div className="flex justify-between items-center">
                        <h1 className="text-lg font-bold mb-2 conthrax-font benefits-tooltip-target">
                          Benefits Received
                        </h1>
                        <Tooltip
                          target=".benefits-tooltip-target"
                          content="Represents the cumulative amount received from the insurance policy, including income and/or death benefit."
                          className="max-w-[600px]"
                          position="top"
                        />
                        <ChartFilter />
                      </div>
                      <div className="flex gap-2">
                        {["income", "db"].map((dataType, index) => (
                          <div className="flex items-center gap-2" key={index}>
                            <AnimatedCheckbox
                              checked={selectedDataTypes.includes(dataType)}
                              onChange={() => handleDataTypeChange(dataType)}
                              inputId={dataType}
                              label={dataType === "income" ? "Income" : "Death Benefit"}
                              brandingColor={isDarkMode ? secondaryColor : primaryColor}
                              isDisabled={isPlanOrInactive}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                    {sdChartsData ? (
                      <Chart
                        type="line"
                        data={{
                          labels: getChartLabels(proposalValuation, selectedChartFilter),
                          datasets: sdChartsData,
                        }}
                        options={splitDollarChartOptions}
                        key={`${isDarkMode}`}
                        height="260px"
                      />
                    ) : (
                      <div className="flex justify-center items-center h-full">
                        <p className="text-lg text-center">No data available</p>
                      </div>
                    )}
                  </>
                )}
              </Card>
            </div>
          </div>
        )}
      </CommentLayout>
    </>
  );
};

export default InteractiveProposalSummary;
